import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { Filter as FilterIcon } from 'lucide-react';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import FilterDrawer from '../../../shared/FilterDrawer';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import { Pill } from '../../../shared/Pill';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import ParagraphText from '../../../shared/ParagraphText';
import RequiredMark from '../../../shared/RequiredMark';
import { formatDateTime } from '../../../shared/tenantClock';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { askConfirm } from '../../../shared/AppDialogs';

// CRM > Website enquiries (issue #287, vis_public_site.md "General
// Enquiries"): enquiries from the website's Contact page whose email matched
// no business, or more than one. Nothing is in the CRM until staff attach
// one to a business or person, make a Lead from it, or dismiss it. Every
// enquiry stays in the list with its Status; the Waiting tab is the to-do
// view. Trade Enquiries never come here. A row click shows the whole
// message (Inline Row Expand); the staff email's link (?enquiry=<id>) opens
// on that row.

const TH = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' };
const TD = { padding: '6px 8px', fontSize: 14, verticalAlign: 'top' };
const CLAMP = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const fieldWrapStyle = { marginBottom: 14 };
const FILTER_SCREEN_KEY = 'crm-website-enquiries';
const DEFAULT_FILTERS = { status: 'all', topic: '' };
const SYSTEM_TABS = [{ key: 'waiting', label: 'Waiting', filters: { status: 'waiting' } }];
const FIELD_FILTER_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_FILTER_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };
const STATUS_PILLS = {
  waiting: { label: 'Waiting', color: '#92400e', background: '#fef3c7' },
  attached: { label: 'Attached', color: '#1b6e3a', background: '#e3f4e8' },
  lead: { label: 'Lead', color: '#1b6e3a', background: '#e3f4e8' },
  dismissed: { label: 'Dismissed', color: '#5a6b85', background: '#eef1f5' },
};

// Same react-select chrome as MoveActivityDrawer's entity picker.
const entitySelectStyles = {
  container: (base) => ({ ...base, width: '100%' }),
  control: (base) => ({
    ...base,
    minHeight: 30,
    borderRadius: 4,
    borderColor: '#a9a9a9',
    boxShadow: 'none',
    fontSize: 14,
    '&:hover': { borderColor: '#a9a9a9' },
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  option: (base) => ({ ...base, fontSize: 14 }),
};

function loadBusinessOptions(inputValue, callback) {
  if (!inputValue) return callback([]);
  axios.get('/api/commercial-entities/quick-search', { params: { q: inputValue } })
    .then((res) => callback((res.data.data || []).map((r) => ({
      value: String(r.id), label: r.type === 'individual' ? `${r.name} (person)` : r.name, name: r.name, type: r.type,
    }))))
    .catch(() => callback([]));
}

function EnquirySummary({ enquiry }) {
  return (
    <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', borderRadius: 6, padding: '10px 12px', marginBottom: 16, fontSize: 13, color: '#334155' }}>
      <div style={{ fontWeight: 600 }}>{enquiry.name}</div>
      <div>{enquiry.email}{enquiry.phone ? ` · ${enquiry.phone}` : ''}</div>
      {enquiry.business_name && <div>{enquiry.business_name}</div>}
      <div style={{ marginTop: 4, color: '#5a6b85' }}>{enquiry.topic_name}</div>
    </div>
  );
}

// The people currently linked to a business, either way round.
function peopleOn(entity) {
  const people = [
    ...(entity.links || []).filter((l) => !l.ended_at).map((l) => l.related_entity),
    ...(entity.incoming_links || []).filter((l) => !l.ended_at).map((l) => l.commercial_entity),
  ].filter((p) => p && p.type === 'individual');
  return people.filter((p, i) => people.findIndex((q) => q.id === p.id) === i)
    .map((p) => ({ id: p.id, name: p.name || [p.first_name, p.last_name].filter(Boolean).join(' ') }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// Attach, or Create Lead, for one enquiry. Attach to a business records the
// enquiry against the sender as a new person, against a person already on
// the business (who gains the sender's email, so a known person writing
// from a new address isn't duplicated), or on the business alone. Attach to
// a person records it on them, and they gain the sender's email.
function EnquiryActionDrawer({ mode, enquiry, onClose, onDone }) {
  const [business, setBusiness] = useState(null);
  const [recordAgainst, setRecordAgainst] = useState('new'); // 'new' | 'none' | a person's id
  const [people, setPeople] = useState([]);
  const [leadName, setLeadName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const open = !!(mode && enquiry);
  const isAttach = mode === 'attach';
  const isOrganisation = business?.type === 'organization';

  useEffect(() => {
    if (!open) return;
    setBusiness(null);
    setRecordAgainst('new');
    setPeople([]);
    setLeadName(enquiry.business_name || enquiry.name);
    setError('');
  }, [open, enquiry]);

  // A person with the sender's name is picked for them; staff can change it.
  useEffect(() => {
    if (!business || business.type !== 'organization') { setPeople([]); return; }
    let cancelled = false;
    axios.get(`/api/commercial-entities/${business.value}`)
      .then((res) => {
        if (cancelled) return;
        const list = peopleOn(res.data.data || {});
        setPeople(list);
        const sameName = list.find((p) => p.name.trim().toLowerCase() === String(enquiry?.name || '').trim().toLowerCase());
        setRecordAgainst(sameName ? String(sameName.id) : 'new');
      })
      .catch(() => { if (!cancelled) setPeople([]); });
    return () => { cancelled = true; };
  }, [business, enquiry]);

  const canSave = isAttach ? !!business : !!leadName.trim();

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const res = isAttach
        ? await axios.post(`/api/website-enquiries/${enquiry.id}/attach`, {
          business_id: business.value,
          add_contact: isOrganisation && recordAgainst !== 'none',
          contact_id: !isOrganisation || ['new', 'none'].includes(recordAgainst) ? null : Number(recordAgainst),
        })
        : await axios.post(`/api/website-enquiries/${enquiry.id}/create-lead`, { name: leadName.trim() });
      onDone({ businessId: res.data.data.businessId, businessName: isAttach ? business.name : leadName.trim(), isLead: !isAttach });
    } catch (err) {
      setError(err.response?.data?.message || (isAttach ? 'Attach failed' : 'Create Lead failed'));
    }
    setSaving(false);
  };

  return (
    <FilterDrawer
      open={open}
      onOpenChange={(next) => { if (!next) onClose(); }}
      title={isAttach ? 'Attach' : 'Create Lead'}
      description={isAttach
        ? 'Log this enquiry on an existing business or person.'
        : 'Add a new Organisation Lead, Source Website Enquiry, with the sender as its contact.'}
      footer={(
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <CancelButton onClick={onClose} disabled={saving} />
          <SaveButton onClick={handleSave} loading={saving} disabled={!canSave} type="button" />
        </div>
      )}
    >
      {enquiry && <EnquirySummary enquiry={enquiry} />}
      {isAttach ? (
        <>
          <div style={fieldWrapStyle}>
            <label style={fieldLabelStyle}>Business or person<RequiredMark /></label>
            <AsyncSelect
              cacheOptions
              defaultOptions={false}
              loadOptions={loadBusinessOptions}
              value={business}
              onChange={setBusiness}
              placeholder="Search businesses and people..."
              styles={entitySelectStyles}
              noOptionsMessage={() => 'No matches found'}
              loadingMessage={() => 'Searching...'}
            />
          </div>
          {isOrganisation && (
            <div style={fieldWrapStyle}>
              <label style={fieldLabelStyle} htmlFor="attach-record-against">Record against</label>
              <select id="attach-record-against" className="styled-input" value={recordAgainst} onChange={(e) => setRecordAgainst(e.target.value)} style={{ width: '100%', fontSize: 14 }}>
                <option value="new">{enquiry?.name || 'The sender'}, as a new contact</option>
                {people.map((p) => <option key={p.id} value={String(p.id)}>{p.name}, adding {enquiry?.email}</option>)}
                <option value="none">The business only</option>
              </select>
              <div style={{ fontSize: 12, color: '#9aa5b5', marginTop: 4 }}>
                Pick a person already on the business when it&apos;s them writing from a new email.
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={fieldWrapStyle}>
          <label style={fieldLabelStyle}>Lead name<RequiredMark /></label>
          <input className="styled-input" value={leadName} onChange={(e) => setLeadName(e.target.value)} maxLength={255} style={{ width: '100%', fontSize: 14 }} />
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 10 }}>{error}</div>}
    </FilterDrawer>
  );
}

function WebsiteEnquiriesPage() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [searchParams] = useSearchParams();
  const linkedId = searchParams.get('enquiry');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const { status, topic } = filters;
  const applyFilters = (values) => { setFilters(values); setLogged(null); };
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const [filterDraft, setFilterDraft] = useState(null); // null = panel closed
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };
  const [topics, setTopics] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(linkedId ? Number(linkedId) : null);
  const [action, setAction] = useState({ mode: null, enquiry: null });
  const [logged, setLogged] = useState(null);

  useEffect(() => {
    axios.get('/api/enquiry-topics', { params: { status: 'all' } })
      .then((res) => setTopics(res.data.data || []))
      .catch(() => setTopics([]));
  }, []);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/website-enquiries', { params: { status, topic: topic || undefined } });
      setRows(res.data.data || []);
    } catch (err) {
      setRows([]);
      showError(err, "Couldn't load Website enquiries");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [status, topic]);

  const post = async (enquiry, verb, failMessage) => {
    try {
      await axios.post(`/api/website-enquiries/${enquiry.id}/${verb}`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, failMessage);
      fetchRows();
    }
  };

  const handleDismiss = async (enquiry) => {
    if (!(await askConfirm(`Dismiss the enquiry from ${enquiry.name}? Nothing is added to the CRM. You can restore it later.`, { confirmLabel: 'Dismiss' }))) return;
    post(enquiry, 'dismiss', 'Dismiss failed');
  };

  const handleDone = (result) => {
    const { enquiry } = action;
    fetchRows();
    setAction({ mode: null, enquiry: null });
    setLogged({ ...result, sender: enquiry.name });
    dismissError();
  };

  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_FILTER_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
          <option value="all">All</option>
          <option value="waiting">Waiting</option>
          <option value="dealt">Dealt with</option>
          <option value="attached">Attached</option>
          <option value="lead">Lead</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>
      <div>
        <label style={FIELD_FILTER_LABEL_STYLE}>Topic</label><br />
        <select className="styled-input" value={draft.topic || ''} onChange={(e) => setDraftField('topic', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
          <option value="">All topics</option>
          {topics.map((t) => <option key={t.id} value={t.name}>{t.name}{t.is_active ? '' : ' (deactivated)'}</option>)}
        </select>
      </div>
    </>
  );

  const colCount = 8;

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Website Enquiries</h2>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div style={{ margin: '14px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
          systemTabs={SYSTEM_TABS}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the Website enquiries list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      {logged && (
        <div style={{ margin: '16px 24px 0', padding: '10px 14px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, fontSize: 14, color: '#065f46' }}>
          {logged.sender}&apos;s enquiry is logged on {logged.isLead ? 'the new Lead ' : ''}
          <Link to={`/commercial-entities/${logged.businessId}`}>{logged.businessName}</Link>.
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 180 }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '12%' }} />
            <col />
            <col style={{ width: '14%' }} />
            <col style={{ width: 56 }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={TH}>Arrived</th>
              <th style={TH}>Name</th>
              <th style={TH}>Email</th>
              <th style={TH}>Business name</th>
              <th style={TH}>Topic</th>
              <th style={TH}>Message</th>
              <th style={TH}>Status</th>
              <th style={TH}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={colCount} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((row) => {
              const isExpanded = expandedId === row.id;
              const pill = STATUS_PILLS[row.status] || STATUS_PILLS.waiting;
              const dealtWith = row.status !== 'waiting';
              const muted = dealtWith ? { color: '#8a94a6' } : {};
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className="list-row"
                    onClick={() => setExpandedId(isExpanded ? null : row.id)}
                    style={{ cursor: 'pointer', background: String(row.id) === linkedId ? '#fffbeb' : undefined }}
                    title={isExpanded ? 'Click to hide the message' : 'Click to read the whole message'}
                  >
                    <td style={{ ...TD, whiteSpace: 'nowrap', ...muted }}>{formatDateTime(row.created_at)}</td>
                    <td style={{ ...TD, ...CLAMP, fontWeight: 600, ...muted }}>{row.name}</td>
                    <td style={{ ...TD, ...CLAMP, ...muted }}>{row.email}</td>
                    <td style={{ ...TD, ...CLAMP, ...muted }}>{row.business_name || '—'}</td>
                    <td style={{ ...TD, ...CLAMP, ...muted }}>{row.topic_name}</td>
                    <td style={{ ...TD, ...CLAMP, color: dealtWith ? '#8a94a6' : '#475569' }}>{row.message}</td>
                    <td style={{ ...TD, ...CLAMP }} onClick={(e) => { if (row.commercial_entity_id) e.stopPropagation(); }}>
                      <Pill color={pill.color} background={pill.background}>{pill.label}</Pill>
                      {row.commercial_entity_id && row.logged_on_name && (
                        <div style={{ ...CLAMP, fontSize: 13, marginTop: 2 }}>
                          <Link to={`/commercial-entities/${row.commercial_entity_id}`} title="Open the record it's logged on">{row.logged_on_name}</Link>
                        </div>
                      )}
                    </td>
                    <td style={{ ...TD, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu
                        items={row.status === 'dismissed'
                          ? [{ key: 'restore', label: 'Restore', needs: 'update', onClick: () => post(row, 'restore', 'Restore failed') }]
                          : dealtWith ? [] : [
                            { key: 'attach', label: 'Attach', needs: 'update', onClick: () => setAction({ mode: 'attach', enquiry: row }) },
                            { key: 'create-lead', label: 'Create Lead', needs: 'update', onClick: () => setAction({ mode: 'lead', enquiry: row }) },
                            { key: 'dismiss', label: 'Dismiss', needs: 'update', danger: true, onClick: () => handleDismiss(row) },
                          ]}
                      />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={colCount} style={{ padding: '10px 16px 14px', background: '#f8fafc', borderBottom: '1px solid #e3e7ee', fontSize: 14 }}>
                        <div style={{ color: '#5a6b85', fontSize: 13, marginBottom: 6 }}>
                          {row.email}{row.phone ? ` · ${row.phone}` : ''}
                        </div>
                        <ParagraphText text={row.message} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <EnquiryActionDrawer
        mode={action.mode}
        enquiry={action.enquiry}
        onClose={() => setAction({ mode: null, enquiry: null })}
        onDone={handleDone}
      />
    </div>
  );
}

export default WebsiteEnquiriesPage;
